"use client";

import { Clock, Building2, Mail, MapPin, Minus, Phone, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Locations() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const neighborhoodsLeft = ["Sharjah Industrial Area", "Ajman New Industrial", "Al Ain Industrial Zone", "Sharjah Airport Freezone", "Ajman Port Area", "Al Ain City Center"];
  const neighborhoodsRight = ["Sharjah Muwailih", "Ajman Al Jurf", "Al Ain Sanaiya", "Sharjah Sajaa", "Ajman Al Rawda", "Al Ain Industrial City"];
  const mapMarkers = [{
    id: 1,
    top: "28%",
    left: "32%",
    main: false
  }, {
    id: 2,
    top: "42%",
    left: "58%",
    main: true
  }, {
    id: 3,
    top: "56%",
    left: "24%",
    main: false
  }, {
    id: 4,
    top: "38%",
    left: "72%",
    main: false
  }, {
    id: 5,
    top: "68%",
    left: "48%",
    main: false
  }, {
    id: 6,
    top: "22%",
    left: "62%",
    main: false
  }, {
    id: 7,
    top: "74%",
    left: "70%",
    main: false
  }];
  const contactInfo = [{
    id: "phone",
    label: "CALL",
    value: "+971-527750-331",
    icon: Phone
  }, {
    id: "email",
    label: "EMAIL",
    value: "info@world-sky.ae",
    icon: Mail
  }, {
    id: "address",
    label: "OFFICE",
    value: "Ind Area 11, Fourth Street, Sharjah",
    icon: MapPin
  }, {
    id: "hours",
    label: "HOURS",
    value: "Mon-Sat 8a-7p · Sun Closed",
    icon: Clock
  }];
  return <section data-section-id="288" id="our-uae-locations" className="bg-slate-950 text-white py-24 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default text-xs tracking-[0.2em] text-white mb-6 uppercase">
              Strategic Presence
            </p>

            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-white mb-8 tracking-tighter font-bold">
              Engineering the <br />
              <em className="italic font-light text-white">UAE supply chain</em>
            </h2>

            <p className="font-default text-lg text-white leading-relaxed mb-10 max-w-md">
              World Sky operates a robust logistics network across Sharjah, Ajman, and Al Ain. Our warehouses ensure rapid distribution of premium aluminum profiles to meet the rigorous demands of UAE construction.
            </p>

            {}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-12">
              {[...neighborhoodsLeft, ...neighborhoodsRight].map((name, i) => <div key={name} data-index={i} className="flex items-center gap-3 group">
                  <Building2 className="size-3.5 text-white shrink-0" strokeWidth={1.5} />
                  <span className="font-default text-sm text-white group-hover:text-primary transition-colors">
                    {name}
                  </span>
                </div>)}
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10 pt-8 border-t border-border">
              {contactInfo.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.id} data-index={i} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Icon className="size-3.5 text-white" strokeWidth={1.5} />
                      <span className="font-default text-[10px] tracking-[0.2em] text-white uppercase">
                        {item.label}
                      </span>
                    </div>
                    <span className="font-mono text-sm text-white leading-snug font-medium">
                      {item.value}
                    </span>
                  </div>;
            })}
            </div>

            <Button asChild className="rounded-lg bg-primary text-white hover:bg-primary/90 font-default text-sm h-11 px-7">
              <Link to="/ConnectWithSalesPage">
                Inquire via WhatsApp →
              </Link>
            </Button>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative rounded-lg overflow-hidden shadow-lg border border-border">
              {}
              <div className="relative w-full aspect-[5/4] md:aspect-[6/5]" style={{
              background: "linear-gradient(135deg, hsl(222 47% 10%) 0%, hsl(222 47% 5%) 100%)"
            }}>
                {}
                <div className="absolute inset-0 z-0 opacity-40 grayscale invert">
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3606.56654321!2d55.4333!3d25.3167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5f0000000001%3A0x0!2zMjXCsDE5JzAwLjAiTiA1NcKwMjYnMDAuMCJF!5e0!3m2!1sen!2sae!4v1710000000000!5m2!1sen!2sae" width="100%" height="100%" style={{
                  border: 0
                }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sharjah Office Location"></iframe>
                </div>

                {}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 600 500" preserveAspectRatio="none" aria-hidden="true">
                  {}
                  <path d="M 0 150 Q 200 130 400 180 T 600 200" stroke="hsl(210 40% 98% / 0.05)" strokeWidth="8" fill="none" />
                  <path d="M 0 320 Q 180 340 350 310 T 600 330" stroke="hsl(210 40% 98% / 0.05)" strokeWidth="6" fill="none" />
                  <path d="M 150 0 Q 170 200 200 350 T 220 500" stroke="hsl(210 40% 98% / 0.05)" strokeWidth="6" fill="none" />
                  <path d="M 430 0 Q 450 180 420 320 T 460 500" stroke="hsl(210 40% 98% / 0.05)" strokeWidth="6" fill="none" />

                  {}
                  <polygon points="120,120 480,100 540,250 460,400 180,420 90,280" fill="hsla(199, 89%, 48%, 0.05)" stroke="hsla(199, 89%, 48%, 0.2)" strokeWidth="1.5" strokeDasharray="6 4" />
                </svg>

                {}
                {mapMarkers.map((marker, i) => <div key={marker.id} data-index={i} className="absolute -translate-x-1/2 -translate-y-full z-20" style={{
                top: marker.top,
                left: marker.left
              }}>
                    {marker.main && <>
                        <span className="absolute left-1/2 bottom-0 -translate-x-1/2 size-10 rounded-lg bg-sky-500/20 animate-ping" aria-hidden="true" />
                        <span className="absolute left-1/2 bottom-0 -translate-x-1/2 size-6 rounded-lg bg-sky-500/30 animate-pulse" aria-hidden="true" />
                      </>}
                    <div className="relative">
                      <svg width={marker.main ? "28" : "20"} height={marker.main ? "34" : "24"} viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 18 12 18s12-10 12-18c0-6.6-5.4-12-12-12z" fill="hsl(222 47% 7%)" />
                        <path d="M12 6 C 9 9, 9 13, 12 16 C 15 13, 15 9, 12 6 Z" fill="hsl(199 89% 48%)" />
                      </svg>
                    </div>
                  </div>)}

                {}
                <div className="absolute top-4 right-4 flex flex-col gap-2 z-30">
                  <Button variant="ghost" size="icon" className="size-9 rounded-lg bg-background/95 backdrop-blur-sm border border-border hover:bg-background" aria-label="Zoom in">
                    <Plus className="size-4 text-foreground" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="icon" className="size-9 rounded-lg bg-background/95 backdrop-blur-sm border border-border hover:bg-background" aria-label="Zoom out">
                    <Minus className="size-4 text-foreground" strokeWidth={1.5} />
                  </Button>
                </div>

                {}
                <div className="absolute bottom-4 left-4 bg-background/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow border border-border flex items-center gap-2 z-30">
                  <MapPin className="size-3.5 text-white" strokeWidth={1.5} />
                  <span className="font-default text-[10px] tracking-[0.15em] text-white uppercase font-bold">
                    World Sky HQ · Sharjah
                  </span>
                </div>
              </div>
            </div>

            <p className="font-default text-[10px] md:text-xs tracking-[0.2em] text-white uppercase mt-5 text-center md:text-left">
              Logistics lead time: 24-48 hours · Regional delivery available
            </p>
          </div>
        </div>
      </div>
    </section>;
}